import { useGoals } from 'features/goals';
import { useSortedLocalizedLifeAreas } from 'features/life-areas';
import { useMemo } from 'react';
import { useUser } from 'shared/hooks/use-user';
import { GetGoalsOptions } from 'shared/services/backend-api';
import { Goal } from 'shared/types/goal';
import { GoalSortingMode, GroupByMode } from 'shared/types/sorting-mode';
import { GoalRoadmapSortingKeys } from 'shared/types/user-settings';
import { sortGoals } from 'shared/utils/sort-goals';

export const useSortedGoals = (options?: GetGoalsOptions) => {
  const user = useUser();
  const goals = useGoals(options);
  const sortedLifeAreas = useSortedLocalizedLifeAreas();

  return useMemo(() => {
    if (!goals?.length) {
      return [];
    }

    const rootGoals = goals.filter((goal) => !goal.parentIds?.length);

    // Build order array based on goalGroupBy setting
    const customOrder =
      user?.settings?.goalRoadmapSorting?.[GoalRoadmapSortingKeys.InProgress] ??
      [];

    // Sort root goals using getSortedGoals
    const validLifeAreaIds = new Set(sortedLifeAreas.map((la) => la.id));
    const lifeAreaOrder = new Map(
      sortedLifeAreas.map((la, index) => [la.id, index]),
    );

    const sortedRootGoals =
      user?.settings?.goalGroupBy === GroupByMode.LifeArea
        ? [
            ...sortedLifeAreas
              .map((lifeArea) =>
                sortGoals(
                  rootGoals.filter(
                    ({ lifeAreaId }) => lifeAreaId === lifeArea.id,
                  ),
                  {
                    sortMode:
                      user?.settings?.goalSortingMode ??
                      GoalSortingMode.StartDate,
                    allGoals: goals,
                    customOrder: lifeArea.goalSorting ?? [],
                  },
                ),
              )
              .flat(),
            ...sortGoals(
              rootGoals.filter(
                ({ lifeAreaId }) =>
                  !lifeAreaId || !validLifeAreaIds.has(lifeAreaId),
              ),
              {
                allGoals: goals,
                sortMode:
                  user?.settings?.goalSortingMode ?? GoalSortingMode.StartDate,
                customOrder:
                  user?.settings?.goalGroupSorting?.lifeArea?.sorting?.[
                    'null'
                  ] ?? [],
              },
            ),
          ]
        : sortGoals(rootGoals, {
            allGoals: goals,
            sortMode:
              user?.settings?.goalSortingMode ?? GoalSortingMode.StartDate,
            customOrder,
            lifeAreaOrder,
          });

    // Process goals hierarchically: children use the global sort mode so the
    // main list reorders uniformly. parent.goalSorting is still passed as
    // customOrder so per-parent drag-order is respected when the global mode
    // is Custom.
    const childSortMode =
      user?.settings?.goalSortingMode ?? GoalSortingMode.StartDate;

    const processGoals = (parentGoals: Goal[]): Goal[] => {
      return parentGoals.flatMap((parentGoal) => {
        const childGoals = goals.filter(
          (goal) =>
            goal.parentIds?.[goal.parentIds.length - 1] === parentGoal.id,
        );

        const sortedChildGoals = sortGoals(childGoals, {
          sortMode: childSortMode,
          customOrder: parentGoal.goalSorting ?? [],
          allGoals: goals,
        });

        return [parentGoal, ...processGoals(sortedChildGoals)];
      });
    };

    return processGoals(sortedRootGoals);
  }, [
    goals,
    sortedLifeAreas,
    user?.settings?.goalGroupBy,
    user?.settings?.goalGroupSorting?.lifeArea?.sorting,
    user?.settings?.goalRoadmapSorting,
    user?.settings?.goalSortingMode,
  ]);
};
